import {
  Box, Chip, Divider, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import type { IntakeFormDef, PublicIntakeDraft } from '../../../../types/intake';
import { testParamsSuffix } from '../intakeFormState';

interface Props {
  def: IntakeFormDef;
  draft: PublicIntakeDraft;
}

/**
 * Read-only summary of the whole request — project block + every sample with
 * its requested tests. Used by the Review & confirm step AND the post-submit
 * view, so what the client reviews is exactly what they see after submitting.
 */
export const RequestSummary = ({ def, draft }: Props) => {
  const testLabel = new Map<string, { name: string; astm: string | null }>();
  for (const cat of def.categories) {
    for (const t of cat.tests) testLabel.set(t.key.toUpperCase(), { name: t.name, astm: t.astm });
  }
  const sampleTypeLabel = new Map(def.sampleTypeOptions.map((o) => [o.code, o.description]));

  const info: [string, string | null][] = [
    ['Project Name', draft.projectName],
    ['Project Number', draft.projectId],
    ['Project Location', draft.projectLocation],
    ['Client Name', draft.clientName],
    ['Contact Person', draft.contactName],
    ['Contact Email', draft.contactEmail],
    ['Due Date', draft.dueDate],
  ];

  const totalTests = draft.samples.reduce(
    (acc, s) => acc + s.tests.reduce((a, t) => a + t.quantity, 0), 0,
  );

  return (
    <>
      {/* Project information */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
          Project information
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mt: 0.5 }}>
          {info.map(([label, value]) => (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || '—'}</Typography>
            </Box>
          ))}
        </Box>
        {draft.clientNotes && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary">Comments for the lab</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{draft.clientNotes}</Typography>
          </>
        )}
      </Paper>

      {/* Samples & tests */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, px: 2, pt: 1.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
            Samples & tests
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {draft.samples.length} sample{draft.samples.length === 1 ? '' : 's'} · {totalTests} test{totalTests === 1 ? '' : 's'} requested
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary' } }}>
                <TableCell>#</TableCell>
                <TableCell>Location ID</TableCell>
                <TableCell align="right">Top (m)</TableCell>
                <TableCell align="right">Base (m)</TableCell>
                <TableCell>Sample Ref</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Requested tests</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {draft.samples.map((s, i) => (
                <TableRow key={i}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{s.locationId ?? '—'}</TableCell>
                  <TableCell align="right">{s.topDepth != null ? Number(s.topDepth).toFixed(2) : '—'}</TableCell>
                  <TableCell align="right">{s.baseDepth != null ? Number(s.baseDepth).toFixed(2) : '—'}</TableCell>
                  <TableCell>{s.sampleRef ?? '—'}</TableCell>
                  <TableCell>{(s.sampleType && sampleTypeLabel.get(s.sampleType)) ?? '—'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {s.tests.length === 0 && (
                        <Typography variant="caption" color="text.secondary">
                          none — this sample will be ignored
                        </Typography>
                      )}
                      {s.tests.map((t) => {
                        const label = testLabel.get(t.testKey.toUpperCase());
                        return (
                          <Chip
                            key={t.testKey}
                            size="small"
                            label={
                              `${t.quantity} × ${label?.name ?? 'Unknown test'}` +
                              (label?.astm ? ` (${label.astm})` : '') +
                              testParamsSuffix(t.params)
                            }
                            sx={{ fontSize: '0.7rem' }}
                          />
                        );
                      })}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};
